/**
 * NIGHTBOUND - sign in / create account.
 *
 * One screen, two modes. Validation failures come back from the server and are
 * shown verbatim; the client never decides whether credentials are valid.
 */

import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useGame } from '../state/GameProvider'
import { Button } from '../components/ui'
import { Logo } from '../components/Logo'

type Mode = 'login' | 'register'

/** Mirrors PASSWORD_MIN_LENGTH on the server (see server/auth/password.ts). */
const PASSWORD_MIN_LENGTH = 8

export function AuthScreen() {
  const { register, login, error, dismissError } = useGame()
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    dismissError()
    setBusy(true)
    try {
      if (mode === 'register') {
        await register({ email: email.trim(), username: username.trim(), password })
      } else {
        await login({ email: email.trim(), password })
      }
    } finally {
      setBusy(false)
    }
  }

  const switchMode = (next: Mode) => {
    setMode(next)
    dismissError()
  }

  return (
    <div className="relative flex min-h-[100svh] flex-col items-center justify-center px-4 py-10">
      <div aria-hidden className="nb-room absolute inset-0 -z-10" />
      <div aria-hidden className="nb-vignette absolute inset-0 -z-10" />

      <main className="w-full max-w-md space-y-6">
        <Link to="/" className="flex justify-center">
          <Logo size="md" tagline="Find your way back" />
        </Link>

        <div className="nb-panel overflow-hidden">
          <div className="flex border-b border-ash-700/50">
            <TabButton active={mode === 'login'} onClick={() => switchMode('login')}>
              Sign in
            </TabButton>
            <TabButton active={mode === 'register'} onClick={() => switchMode('register')}>
              New here
            </TabButton>
          </div>

          <form onSubmit={submit} className="space-y-4 p-5">
            <Field label="Email" htmlFor="email">
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={inputClass}
                placeholder="you@somewhere.dark"
              />
            </Field>

            {mode === 'register' ? (
              <Field label="Name" htmlFor="username" hint="3-20 letters, numbers, dots, dashes or underscores.">
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  className={inputClass}
                  placeholder="What should it call you?"
                />
              </Field>
            ) : null}

            <Field
              label="Password"
              htmlFor="password"
              hint={
                mode === 'register'
                  ? `At least ${PASSWORD_MIN_LENGTH} characters, mixing letters, numbers or symbols.`
                  : undefined
              }
            >
              <input
                id="password"
                type="password"
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={inputClass}
                placeholder="••••••••"
              />
            </Field>

            {error ? (
              <p role="alert" className="rounded-[3px] border border-blood-500/50 bg-blood-950/60 px-3 py-2 text-xs text-blood-400">
                {error}
              </p>
            ) : null}

            <Button type="submit" variant="primary" size="lg" fullWidth disabled={busy}>
              {busy ? 'Please wait…' : mode === 'register' ? 'Create account' : 'Sign in'}
            </Button>

            <p className="text-center text-[11px] leading-relaxed text-ash-500">
              No email is ever sent. This is a single-player shelter: your account exists only to
              keep your creature safe between visits.
            </p>
          </form>
        </div>
      </main>
    </div>
  )
}

const inputClass =
  'w-full rounded-[3px] border border-ash-600/60 bg-night-950/70 px-3 py-2.5 text-sm text-bone-200 placeholder:text-ash-500 focus:border-ember-600/70 focus:outline-none'

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? 'flex-1 border-b-2 border-ember-500 bg-night-800/60 px-4 py-3 nb-label text-[10px] text-ember-400'
          : 'flex-1 border-b-2 border-transparent px-4 py-3 nb-label text-[10px] text-ash-400 hover:bg-night-800/40 hover:text-bone-300'
      }
    >
      {children}
    </button>
  )
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string
  htmlFor: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label htmlFor={htmlFor} className="block space-y-1.5">
      <span className="nb-label text-[9px] text-ash-300">{label}</span>
      {children}
      {hint ? <span className="block text-[10px] leading-snug text-ash-500">{hint}</span> : null}
    </label>
  )
}
